import { createPortal } from 'react-dom'
import { fmt12, formatEventDate } from '@/lib/eventTime'
import type { EventDetailDto } from '@lib/dto/event.dto'
import type { WallClock } from '@lib/validators/temporal.validator'
import type { EventTimelineEntryDto } from '@lib/dto/event-timeline.dto'

/**
 * The part of a phase this sheet reads.
 *
 * Narrower than `EventPhaseDto` on purpose: the sheet is about intervals and
 * space names, and asking for the whole phase — its objects, its availability,
 * its lock — would mean a test could only exercise it by building all of that.
 */
export interface PhaseInterval {
  id: string
  label: string
  startsAt: WallClock | null
  endsAt: WallClock | null
  space: { name: string }
}

/** A phase as the sheet needs it: a space, in use between two clock times. */
interface Band {
  id: string
  label: string
  space: string
  startsAt: WallClock
  endsAt: WallClock | null
}

/** One printed line: a time, whatever happens at it, and the spaces in use. */
interface SheetRow {
  at: WallClock
  entry: EventTimelineEntryDto | null
  /** The bands covering this instant — more than one when spaces run at once. */
  bands: Band[]
  /** How many rows this row's band cell spans; 0 when an earlier row spans it. */
  bandSpan: number
}

/** `Sun 22 Aug` — enough to say which day, for rows that are not on the first. */
function shortDay(iso: WallClock): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  return at.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
}

/** The phases that have times, as bands. Untimed phases describe no interval. */
function bandsOf(phases: readonly PhaseInterval[]): Band[] {
  return phases
    .flatMap((phase) => {
      const startsAt = phase.startsAt
      // Narrowed in a binding rather than asserted: `phase.startsAt` is
      // `WallClock | null` and a flatMap callback does not carry the guard into
      // the object literal.
      return startsAt === null
        ? []
        : [{ id: phase.id, label: phase.label, space: phase.space.name, startsAt, endsAt: phase.endsAt }]
    })
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
}

/** The bands covering an instant. A band with no end runs to the day's close. */
function coveringAt(bands: Band[], at: WallClock): Band[] {
  return bands.filter((band) => band.startsAt <= at && (band.endsAt === null || at < band.endsAt))
}

/**
 * The sheet's rows: every moment, plus a row where a space comes into use.
 *
 * A phase that starts between two moments earns a line of its own, so the band
 * beside it begins where it actually begins rather than at whatever happened
 * next. A phase starting at the same minute as a moment shares that line.
 *
 * Consecutive rows covered by the same spaces are merged into one spanning cell,
 * which is what makes the left column read as a bracket around the moments
 * inside it rather than as the same words repeated down the page.
 */
export function sheetRows(phases: readonly PhaseInterval[], entries: EventTimelineEntryDto[]): SheetRow[] {
  const bands = bandsOf(phases)
  const entryTimes = new Set(entries.map((entry) => entry.startsAt))
  // One line per minute a space opens, however many open on it: two spaces
  // opening together are one moment in the day, and two blank lines at the
  // same time would read as a mistake (and shared the row's key).
  const openings = [...new Set(bands.map((band) => band.startsAt))].filter((opens) => !entryTimes.has(opens))
  const rows: { at: WallClock; entry: EventTimelineEntryDto | null }[] = [
    ...entries.map((entry) => ({ at: entry.startsAt, entry })),
    ...openings.map((opens) => ({ at: opens, entry: null })),
  ].sort((a, b) => {
    const byClock = a.at.localeCompare(b.at)
    if (byClock !== 0) return byClock
    // A space opening sorts above a moment at the same minute; two moments fall
    // through to the server's own tiebreak. Returning a constant here answered
    // the same for (a, b) and (b, a), which leaves same-minute order to the
    // engine — and the printed sheet could then differ from the screen.
    if ((a.entry === null) !== (b.entry === null)) return a.entry === null ? -1 : 1
    return a.entry !== null && b.entry !== null ? a.entry.sortOrder - b.entry.sortOrder : 0
  })

  const withBands = rows.map((row) => ({ ...row, bands: coveringAt(bands, row.at) }))
  const key = (row: { bands: Band[] }): string =>
    row.bands
      .map((band) => band.id)
      .sort()
      .join('|')

  // Walk backwards so each run's first row learns how far it reaches.
  const spans = new Array<number>(withBands.length).fill(0)
  for (let i = withBands.length - 1; i >= 0; i--) {
    const here = withBands[i]
    const next = withBands[i + 1]
    if (here === undefined) continue
    spans[i] = next !== undefined && key(here) === key(next) ? (spans[i + 1] ?? 0) + 1 : 1
  }
  let covered = 0
  return withBands.map((row, i) => {
    if (covered > 0) {
      covered -= 1
      return { ...row, bandSpan: 0 }
    }
    const span = spans[i] ?? 1
    covered = span - 1
    return { ...row, bandSpan: span }
  })
}

/**
 * The run sheet as it is printed: the thing a coordinator carries.
 *
 * Authored rather than coerced from the editor on screen. That list is made of
 * text inputs, and an input prints as an empty-looking box — printing the screen
 * would produce a page of rectangles.
 *
 * The spaces are set beside the day rather than folded into it. On screen the two
 * are one interleaved list, which reads well when you can scroll; on paper there
 * is room for a column, and a coordinator holding this wants to see at a glance
 * that the Grand Entrance at 6:30 falls inside a Reception that opened at 6:00 —
 * a bracket around the moments, not another line among them.
 *
 * Rendered straight into `<body>`, beside the app rather than inside it, so that
 * printing can switch the app off with `display: none` and lay the sheet out on
 * its own (`index.css`, "The printed run sheet"). Inside the app, the only way to
 * leave it showing was to hide everything else with `visibility`, which keeps
 * every hidden box's height: a long event page printed its sheet, then pages of
 * blank paper.
 */
export function EventRunSheet({ event, entries }: { event: EventDetailDto; entries: EventTimelineEntryDto[] }) {
  const rows = sheetRows(event.phases, entries)
  // `event.date` is already a `CalendarDay`; slicing it produced a bare string
  // that happened to be equal, which is the kind of coincidence a brand exists to
  // stop being load-bearing.
  const eventDay = event.date
  const anyBands = rows.some((row) => row.bands.length > 0)

  return createPortal(
    <div className="run-sheet" aria-hidden="true">
      <header className="run-sheet__head">
        <h1>{event.name}</h1>
        <p>
          {event.venueName}
          {event.date === null ? '' : ` · ${formatEventDate(event.date)}`}
          {event.guestCount > 0 ? ` · ${String(event.guestCount)} guests` : ''}
        </p>
      </header>

      <table className="run-sheet__table">
        <thead>
          <tr>
            <th className="run-sheet__time">Time</th>
            <th>The day</th>
            {anyBands ? <th className="run-sheet__spaces">Spaces</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const onAnotherDay = eventDay !== null && row.at.slice(0, 10) !== eventDay
            return (
              <tr key={row.entry ? row.entry.id : `at-${row.at}`}>
                <td className="run-sheet__time">{fmt12(row.at)}</td>
                <td className="run-sheet__what">
                  {row.entry ? (
                    <>
                      {row.entry.label}
                      {/* The small hours belong to the next morning, and a sheet
                          that did not say so reads as an hour before breakfast. */}
                      {onAnotherDay ? <span className="run-sheet__day"> {shortDay(row.at)}</span> : null}
                    </>
                  ) : null}
                </td>
                {anyBands && row.bandSpan > 0 ? (
                  <td className="run-sheet__spaces" rowSpan={row.bandSpan}>
                    {row.bands.map((band) => (
                      <span key={band.id} className="run-sheet__band">
                        {band.label === band.space ? band.space : `${band.space} — ${band.label}`}
                      </span>
                    ))}
                  </td>
                ) : null}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>,
    document.body,
  )
}
